import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Sparkles, Package, AlertCircle, Rocket } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EUR = (v, c = "EUR") => new Intl.NumberFormat("de-DE", { style: "currency", currency: c || "EUR" }).format(v || 0);

const STAGE_LABELS = {
  neu: "Neu", angebot: "Angebot", verhandlung: "Verhandlung",
  gewonnen: "Gewonnen", verloren: "Verloren",
};

export default function OfferPublic() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/public/offers/${token}`)
      .then((r) => setState({ loading: false, data: r.data, error: null }))
      .catch((e) =>
        setState({
          loading: false, data: null,
          error: e.response?.status === 404 ? "notfound" : "error",
        })
      );
  }, [token]);

  if (state.loading) {
    return <div className="min-h-screen grid place-items-center bg-[#0b0f17] text-slate-400" data-testid="offer-loading">Lade Angebot…</div>;
  }

  if (state.error === "notfound") {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0b0f17] text-slate-200 p-6" data-testid="offer-notfound">
        <Card className="max-w-md w-full bg-[#111827] border-white/5">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-10 w-10 text-amber-400 mx-auto mb-4" />
            <h1 className="text-2xl font-extrabold tracking-tight">Angebot nicht gefunden</h1>
            <p className="text-sm text-slate-400 mt-3">
              Der Link ist entweder ungültig oder abgelaufen. Bitte kontaktiere den Absender des Angebots für einen neuen Link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0b0f17] text-slate-200 p-6">
        <div className="text-sm text-slate-400">Angebot konnte nicht geladen werden.</div>
      </div>
    );
  }

  const { brand, offer } = state.data;
  const sumProducts = (offer.products || []).reduce((a, p) => a + (p.price || 0), 0);

  return (
    <div className="min-h-screen bg-[#0b0f17] text-slate-100" data-testid="offer-public-page">
      {/* Header */}
      <header className="border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-500/20 text-emerald-400 grid place-items-center">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <div className="font-bold tracking-tight text-white">{brand.name}</div>
              <div className="text-[11px] text-slate-500">{brand.tagline}</div>
            </div>
          </div>
          <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 border font-normal">
            Angebot
          </Badge>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        <div className="text-xs font-semibold uppercase tracking-wider text-emerald-400">Persönliches Angebot</div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-2 text-white" data-testid="offer-title">
          {offer.title}
        </h1>
        {offer.lead?.name && (
          <p className="text-slate-400 text-sm mt-3">
            Für: <span className="text-slate-200 font-medium">{offer.lead.name}</span>
            {offer.lead.company ? ` · ${offer.lead.company}` : ""}
          </p>
        )}

        {/* Summary */}
        <div className="grid sm:grid-cols-3 gap-4 mt-8">
          <SummaryTile label="Gesamtwert" value={EUR(offer.value, offer.currency)} testid="offer-value" />
          <SummaryTile label="Stufe" value={STAGE_LABELS[offer.stage] || offer.stage || "—"} testid="offer-stage" />
          <SummaryTile label="Gültig bis" value={offer.expected_close_date ? new Date(offer.expected_close_date).toLocaleDateString("de-DE") : "—"} testid="offer-valid" />
        </div>

        {/* Products */}
        <section className="mt-12">
          <h2 className="text-lg font-bold tracking-tight text-white mb-4">Enthaltene Produkte</h2>
          {(offer.products || []).length === 0 ? (
            <Card className="bg-[#111827] border-white/5">
              <CardContent className="p-6 text-sm text-slate-400">Keine Produkte zugeordnet.</CardContent>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="offer-products">
              {offer.products.map((p) => (
                <Card key={p.id} className="bg-[#111827] border-white/5 overflow-hidden" data-testid={`offer-product-${p.id}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.name} className="h-16 w-16 rounded-md object-cover shrink-0" />
                    ) : (
                      <div className="h-16 w-16 rounded-md bg-white/5 grid place-items-center shrink-0">
                        <Package className="h-5 w-5 text-slate-500" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-white truncate">{p.name}</div>
                      <div className="text-xs text-slate-400 truncate">{p.description}</div>
                      {p.category && <div className="text-[10px] text-slate-500 mt-1 uppercase tracking-wider">{p.category}</div>}
                    </div>
                    <div className="text-right">
                      <div className="text-white font-semibold tabular-nums">{EUR(p.price, p.currency)}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              <div className="flex justify-end pt-4 border-t border-white/5 text-sm text-slate-400">
                <div>Einzelsumme Produkte: <span className="text-white font-semibold tabular-nums">{EUR(sumProducts, offer.currency)}</span></div>
              </div>
            </div>
          )}
        </section>

        {offer.notes && (
          <section className="mt-10">
            <h2 className="text-lg font-bold tracking-tight text-white mb-3">Hinweise</h2>
            <Card className="bg-[#111827] border-white/5">
              <CardContent className="p-5 text-sm text-slate-300 whitespace-pre-wrap" data-testid="offer-notes">
                {offer.notes}
              </CardContent>
            </Card>
          </section>
        )}

        <section className="mt-14 pt-6 border-t border-white/5 text-xs text-slate-500 flex flex-wrap items-center justify-between gap-2" data-testid="offer-footer-brand">
          <div className="flex items-center gap-1.5 text-slate-600">
            <span>Erstellt mit</span>
            <span className="font-semibold text-slate-400">Marketing Rocket</span>
            <Rocket className="h-3.5 w-3.5 text-emerald-400" />
          </div>
          <div>
            <div>
              Erstellt am {new Date(offer.created_at).toLocaleDateString("de-DE")} · {brand.name}
            </div>
            <div>
              Kontakt: <a href={`mailto:${brand.contact_email}`} className="text-emerald-400 hover:underline">{brand.contact_email}</a>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function SummaryTile({ label, value, testid }) {
  return (
    <Card className="bg-[#111827] border-white/5" data-testid={testid}>
      <CardContent className="p-5">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-2xl font-extrabold tracking-tight text-white mt-2">{value}</div>
      </CardContent>
    </Card>
  );
}
